/**
 * AI Chat API service
 * Handles communication with the AI Chat Lambdas:
 *   - the streaming function URL (REACT_APP_AI_CHAT_STREAM_URL, NDJSON events);
 *   - the legacy buffered POST /ai-chat on the API gateway (fallback);
 *   - conversation list / history and feedback on the API gateway.
 * Follows the same token getter pattern as other API services.
 */

const API_URL = process.env.REACT_APP_API_URL || '';

let getAccessTokenFn: ((forceRefresh?: boolean) => Promise<string | null>) | null = null;

export function setAIChatTokenGetter(fn: (forceRefresh?: boolean) => Promise<string | null>) {
  getAccessTokenFn = fn;
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  if (!getAccessTokenFn) return {};
  const token = await getAccessTokenFn();
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
}

/** Read at call time so tests (and a build without the backend) can control it. */
export function getStreamUrl(): string {
  return (process.env.REACT_APP_AI_CHAT_STREAM_URL || '').trim();
}

export interface AIChatResponse {
  conversationId: string;
  messageIndex: number;
  response: string;
  toolsUsed?: string[];
}

export interface ConversationSummary {
  conversationId: string;
  createdAt: string;
  lastMessage: string;
  messageCount: number;
}

/** One row of GET /ai-chat/conversations?conversationId=X. */
export interface HistoryRow {
  messageIndex: number;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  toolsUsed?: string[];
}

export interface HistoryPage {
  conversationId: string;
  messages: HistoryRow[];
  nextBefore: number | null;
}

// ---------------------------------------------------------------------------
// Streaming protocol (mirrors lambda/ai-chat/stream-protocol.ts)

export interface RunMetrics {
  modelId: string;
  rounds: number;
  toolsUsed: string[];
  toolCalls: number;
  exitPath: 'normal' | 'forced_final' | 'timeout_report';
  wallClockMs: number;
}

export type StreamEvent =
  | { type: 'start'; conversationId: string; messageIndex: number }
  | { type: 'progress'; round: number; tool: string; locations: number[]; headline: string }
  | { type: 'delta'; round: number; text: string }
  | { type: 'heartbeat' }
  | { type: 'done'; conversationId: string; messageIndex: number; metrics: RunMetrics }
  | { type: 'error'; message: string };

export type StreamOutcome =
  /** `done` arrived: the answer is saved under these ids. */
  | { kind: 'done'; conversationId: string; messageIndex: number; metrics: RunMetrics }
  /** The server sent an `error` event after the stream opened (the question was received). */
  | { kind: 'error'; message: string }
  /** A JSON rejection before any event (401/403/409/429...). 409 carries the accepted ids. */
  | { kind: 'rejected'; status: number; error?: string; message?: string; conversationId?: string; messageIndex?: number; retryAfterSeconds?: number }
  /** REACT_APP_AI_CHAT_STREAM_URL is not set in this build. */
  | { kind: 'unavailable' }
  /** The request failed before the first event (network, CORS/CSP, 404, non-JSON): safe to fall back. */
  | { kind: 'failed-before-events'; reason: string }
  /** The caller aborted (Stop). The Lambda finishes and saves the answer regardless. */
  | { kind: 'aborted' }
  /** The stream ended without `done` or `error` (connection dropped). */
  | { kind: 'incomplete' };

export interface StreamRequest {
  message: string;
  conversationId?: string;
  clientRequestId: string;
  signal?: AbortSignal;
  onEvent: (event: StreamEvent) => void;
}

function parseEventLine(line: string): StreamEvent | null {
  const trimmed = line.trim();
  if (!trimmed) return null;
  try {
    const parsed = JSON.parse(trimmed);
    return parsed && typeof parsed.type === 'string' ? (parsed as StreamEvent) : null;
  } catch {
    return null;
  }
}

/**
 * POST to the streaming function URL and dispatch NDJSON events as they arrive.
 * Never throws: every way the call can end is a StreamOutcome.
 */
export async function streamAIChatMessage(request: StreamRequest): Promise<StreamOutcome> {
  const url = getStreamUrl();
  if (!url) return { kind: 'unavailable' };

  let response: Response;
  try {
    const authHeaders = await getAuthHeaders();
    response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ message: request.message, conversationId: request.conversationId, clientRequestId: request.clientRequestId }),
      signal: request.signal,
    });
  } catch (err: any) {
    if (err?.name === 'AbortError') return { kind: 'aborted' };
    return { kind: 'failed-before-events', reason: err?.message || 'Network error' };
  }

  if (!response.ok) {
    let body: any = null;
    try {
      body = await response.json();
    } catch {
      return { kind: 'failed-before-events', reason: `HTTP ${response.status}` };
    }
    if (!body || typeof body !== 'object') return { kind: 'failed-before-events', reason: `HTTP ${response.status}` };
    return {
      kind: 'rejected',
      status: response.status,
      error: body.error,
      message: body.message,
      conversationId: body.conversationId,
      messageIndex: body.messageIndex,
      retryAfterSeconds: body.retryAfterSeconds,
    };
  }

  if (!response.body) return { kind: 'failed-before-events', reason: 'No response body' };

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let sawEvent = false;
  let outcome: StreamOutcome | null = null;

  const handle = (event: StreamEvent | null) => {
    if (!event) return;
    sawEvent = true;
    request.onEvent(event);
    if (event.type === 'done') outcome = { kind: 'done', conversationId: event.conversationId, messageIndex: event.messageIndex, metrics: event.metrics };
    if (event.type === 'error') outcome = { kind: 'error', message: event.message };
  };

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let nl: number;
      while ((nl = buffer.indexOf('\n')) >= 0) {
        const line = buffer.slice(0, nl);
        buffer = buffer.slice(nl + 1);
        handle(parseEventLine(line));
      }
    }
    buffer += decoder.decode();
    if (buffer.trim()) handle(parseEventLine(buffer));
  } catch (err: any) {
    if (err?.name === 'AbortError') return { kind: 'aborted' };
    if (!sawEvent) return { kind: 'failed-before-events', reason: err?.message || 'Stream failed' };
    return outcome ?? { kind: 'incomplete' };
  }

  if (outcome) return outcome;
  return sawEvent ? { kind: 'incomplete' } : { kind: 'failed-before-events', reason: 'Empty stream' };
}

// ---------------------------------------------------------------------------
// Legacy buffered path and the other API gateway routes

/**
 * Send a message to the AI Chat over the buffered API Gateway route and get the whole
 * answer back (the fallback when streaming is unavailable).
 */
export async function sendAIChatMessage(
  message: string,
  conversationId?: string,
  clientRequestId?: string,
): Promise<{ success: boolean; data?: AIChatResponse; error?: string; status?: number; errorCode?: string; retryAfterSeconds?: number; conversationId?: string; messageIndex?: number }> {
  try {
    const authHeaders = await getAuthHeaders();
    const response = await fetch(`${API_URL}/ai-chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
      },
      body: JSON.stringify({ message, conversationId, clientRequestId }),
    });

    const data = await response.json();

    if (response.status === 429) {
      return {
        success: false,
        status: 429,
        error: data.message || 'Rate limit exceeded. Please wait before sending more messages.',
        retryAfterSeconds: data.retryAfterSeconds,
      };
    }

    if (response.status === 403) {
      return { success: false, status: 403, error: 'Access denied. AI Chat is restricted.' };
    }

    if (response.status === 409) {
      return {
        success: false,
        status: 409,
        // 'Duplicate request' (the question was already accepted) or 'Conversation busy'.
        errorCode: data.error,
        error: data.message || 'Still answering your last question.',
        conversationId: data.conversationId,
        messageIndex: data.messageIndex,
      };
    }

    if (!response.ok) {
      return { success: false, status: response.status, error: data.message || data.error || 'An error occurred.' };
    }

    return { success: true, data: data.data };
  } catch (error: any) {
    return { success: false, error: error.message || 'Network error. Please try again.' };
  }
}

/**
 * List the user's recent conversations
 */
export async function listAIChatConversations(): Promise<{ success: boolean; data?: ConversationSummary[]; error?: string }> {
  try {
    const authHeaders = await getAuthHeaders();
    const response = await fetch(`${API_URL}/ai-chat/conversations`, {
      headers: { ...authHeaders },
    });

    const data = await response.json();
    if (!response.ok) {
      return { success: false, error: data.error || 'Failed to load conversations.' };
    }

    return { success: true, data: data.data };
  } catch (error: any) {
    return { success: false, error: error.message || 'Network error.' };
  }
}

/**
 * Load one page of a conversation's history (newest 40 rows, or those before `before`).
 * `data` is null when the backend predates the history form (it answers the
 * conversation list, an array, for this query): the caller then shows an empty view.
 */
export async function loadAIChatHistory(
  conversationId: string,
  before?: number,
): Promise<{ success: boolean; data?: HistoryPage | null; error?: string }> {
  try {
    const authHeaders = await getAuthHeaders();
    const params = new URLSearchParams({ conversationId });
    if (before !== undefined) params.set('before', String(before));
    const response = await fetch(`${API_URL}/ai-chat/conversations?${params.toString()}`, {
      headers: { ...authHeaders },
    });

    const data = await response.json();
    if (!response.ok) {
      return { success: false, error: data.error || 'Failed to load the conversation.' };
    }

    const payload = data.data;
    if (Array.isArray(payload) || !payload || !Array.isArray(payload.messages)) {
      return { success: true, data: null };
    }
    return { success: true, data: payload as HistoryPage };
  } catch (error: any) {
    return { success: false, error: error.message || 'Network error.' };
  }
}

/**
 * Submit thumbs up/down feedback for an AI response
 */
export async function submitAIChatFeedback(
  conversationId: string,
  messageIndex: number,
  rating: 'thumbs_up' | 'thumbs_down',
): Promise<{ success: boolean; feedbackId?: string; error?: string }> {
  try {
    const authHeaders = await getAuthHeaders();
    const response = await fetch(`${API_URL}/ai-chat/feedback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
      },
      body: JSON.stringify({ conversationId, messageIndex, rating }),
    });

    const data = await response.json();
    if (!response.ok) {
      return { success: false, error: data.error || 'Failed to submit feedback.' };
    }

    return { success: true, feedbackId: data.feedbackId };
  } catch (error: any) {
    console.error('Feedback submission failed:', error);
    return { success: false, error: 'Network error.' };
  }
}
